import { FC, memo, useEffect,useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { isVipTier,PLAN_LABELS } from '@constants/plans';
import { ROUTING } from '@constants/routing';

import CloseIcon from '@mui/icons-material/Close';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import { Box, IconButton,Typography } from '@mui/material';
import { styled } from '@mui/system';

interface UpgradePromoBannerProps {
  /** Org's current tier id (e.g. 'starter'). */
  currentTier: string | null | undefined;
  /** Lowest tier where VIP routing kicks in (returned by /queue-status). */
  unlockedAtTier: string | null | undefined;
  /** Org id used to scope localStorage dismissal. */
  orgId: string | undefined;
  /** Optional position-aware copy. When omitted, uses generic copy. */
  position?: number;
  /** Optional ETA seconds — surfaced for slightly more concrete copy. */
  etaSeconds?: number;
}

/** localStorage key prefix for banner dismissal. Re-shows after 24h or
 * when the tier changes (key includes the current tier). */
const DISMISS_KEY_PREFIX = 'upgradePromo:dismissed:';
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000;

const StyledBanner = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5, 2),
  borderRadius: 8,
  background: 'linear-gradient(90deg, rgba(48, 84, 255, 0.08) 0%, rgba(132, 80, 255, 0.08) 100%)',
  border: '1px solid rgba(48, 84, 255, 0.2)',
  marginBottom: theme.spacing(2),
}));

const StyledLink = styled(RouterLink)(({ theme }) => ({
  fontWeight: 600,
  textDecoration: 'underline',
  color: theme.palette.primary.main,
  marginLeft: 'auto',
  marginRight: theme.spacing(1),
}));

function formatEta(seconds: number | undefined): string | null {
  if (!seconds || seconds <= 0) return null;
  if (seconds < 60) return `~${seconds}s`;
  const m = Math.round(seconds / 60);
  if (m < 60) return `~${m}min`;
  const h = Math.round(seconds / 3600);
  return `~${h}h`;
}

const UpgradePromoBannerInner: FC<UpgradePromoBannerProps> = ({
  currentTier,
  unlockedAtTier,
  orgId,
  position,
  etaSeconds,
}) => {
  const dismissKey = useMemo(
    () => `${DISMISS_KEY_PREFIX}${orgId ?? 'unknown'}:${currentTier ?? 'unknown'}`,
    [orgId, currentTier],
  );
  const [dismissed, setDismissed] = useState<boolean>(false);

  // Hydrate dismissal flag from localStorage on mount and whenever
  // `dismissKey` changes (e.g. after a tier upgrade).
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(dismissKey);
      if (!raw) {
        setDismissed(false);
        return;
      }
      const ts = Number(raw);
      if (!Number.isFinite(ts) || Date.now() - ts > DISMISS_TTL_MS) {
        window.localStorage.removeItem(dismissKey);
        setDismissed(false);
        return;
      }
      setDismissed(true);
    } catch {
      // Privacy modes / SSR — show the banner.
      setDismissed(false);
    }
  }, [dismissKey]);

  // Hide if: dismissed, no campaign in queue, already on VIP tier, or
  // we don't know what tier to suggest.
  if (dismissed) return null;
  if (!unlockedAtTier) return null;
  if (isVipTier(currentTier)) return null;
  if (typeof position === 'number' && position <= 0) return null;

  const handleDismiss = () => {
    try {
      window.localStorage.setItem(dismissKey, String(Date.now()));
    } catch {
      // ignore
    }
    setDismissed(true);
  };

  const targetLabel = PLAN_LABELS[unlockedAtTier] ?? unlockedAtTier;
  const etaLabel = formatEta(etaSeconds);

  // Phase 1 copy: factual ("reduce wait time"). Phase 3 will swap to
  // "Skip the queue" once VIP routing actually exists.
  const headline =
    typeof position === 'number' && position > 0
      ? `Position ${position} in the queue${etaLabel ? ` · ${etaLabel} estimated wait` : ''}`
      : 'Reduce wait time on busy queues';

  return (
    <StyledBanner role="status">
      <RocketLaunchOutlinedIcon color="primary" fontSize="small" />
      <Box flex="1 1 auto">
        <Typography variant="body2" fontWeight={600}>
          {headline}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Upgrade to {targetLabel} for higher worker priority and faster campaign starts.
        </Typography>
      </Box>
      <StyledLink to={`/${ROUTING.PRICING}`}>See plans</StyledLink>
      <IconButton size="small" aria-label="Dismiss" onClick={handleDismiss}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </StyledBanner>
  );
};

export const UpgradePromoBanner = memo(UpgradePromoBannerInner);
export default UpgradePromoBanner;
